import type MapBrowserEvent from 'ol/MapBrowserEvent';
import DragBox from 'ol/interaction/DragBox';
import type Map from 'ol/Map';
import VectorSource from 'ol/source/Vector';
import type { AppStore } from '../core/store';
import type { AppState, MapTool, SelectionEntry } from '../core/types';
import { fincaIdForSelection } from '../domain/scope';
import { workScopeFragmentIds } from '../domain/scope';
import { deriveDisplaySet } from '../domain/displaySet';
import {
  emptySelectionSet,
  legacySelectionForFocus,
  masterEntryForFragment,
  recordsForSelection,
  replaceSelectionSet,
  selectionEntriesInExtent,
  updateSelectionSet,
} from '../domain/selectionSet';
import { entityFragmentIds, selectGeometryEditFragment } from '../domain/geometryEditing';

/** Everything a selection click may change; restored as a whole when the click turns out to be a double-click. */
type SelectionSnapshot = Pick<AppState, 'selectionSet' | 'selection' | 'genericSelection' | 'casurHydricSelection' | 'activeLayerId' | 'activeFincaId' | 'geometryEditSession' | 'projectDirty'>;
const SNAPSHOT_KEYS: readonly (keyof SelectionSnapshot)[] = ['selectionSet', 'selection', 'genericSelection', 'casurHydricSelection', 'activeLayerId', 'activeFincaId', 'geometryEditSession', 'projectDirty'];
const isSelectionTool = (tool: MapTool): boolean => tool === 'navigate' || tool === 'select' || tool === 'select-multiple';

export class SelectionController {
  private readonly dragBox = new DragBox();
  private tool: MapTool = 'select';
  /** The last two map clicks, with the selection state before each one (null when no selection tool handled it). */
  private clicks: readonly { readonly before: SelectionSnapshot | null }[] = [];

  constructor(private readonly map: Map, private readonly store: AppStore) {
    map.addInteraction(this.dragBox);
    this.dragBox.setActive(false);
    // Selection answers OpenLayers' immediate `click` instead of `singleclick`, which waits a fixed 250 ms to rule
    // out a double-click. OpenLayers pairs a `dblclick` with the two clicks just dispatched: when it arrives, the
    // state from before the first of them is restored, so a double-click still only zooms, exactly as before.
    // While «Mover etiqueta en el mapa» is on, a tap belongs to the label, never to the selection.
    map.on('click', (event) => { if (!map.getTargetElement?.()?.classList.contains('label-drag-mode')) this.onClick(event); });
    map.on('dblclick', () => this.restoreBeforeDoubleClick());
    this.dragBox.on('boxend', () => this.onBoxEnd());
  }

  setTool(tool: MapTool): void {
    this.tool = tool;
    this.dragBox.setActive(tool === 'select-box');
  }

  clear(): void {
    this.store.update({ selectionSet: emptySelectionSet(), selection: null, genericSelection: null, casurHydricSelection: null });
  }

  focusEntry(entry: SelectionEntry, preserveSet = true): void {
    const state = this.store.getState();
    const selectionSet = preserveSet && state.selectionSet.entries.some((item) => item.source === entry.source && item.layerId === entry.layerId && item.kind === entry.kind && item.id === entry.id)
      ? { ...state.selectionSet, focus: entry }
      : updateSelectionSet(state.selectionSet, entry, false);
    this.apply(selectionSet, entry.layerId, { casurHydricSelection: null });
  }

  zoomToSelection(): void {
    const state = this.store.getState();
    const layer = state.layers.find((item) => item.id === state.selectionSet.layerId);
    if (!layer) return;
    const records = recordsForSelection(layer, state.selectionSet);
    if (!records.length) return;
    const source = new VectorSource({ features: records.map(({ feature }) => feature) });
    const extent = source.getExtent();
    if (extent && extent.every(Number.isFinite)) this.map.getView().fit(extent, { padding: [90, 110, 120, 110], duration: 350, maxZoom: 18 });
  }

  private onClick(event: MapBrowserEvent<PointerEvent | KeyboardEvent | WheelEvent>): void {
    const handled = isSelectionTool(this.tool);
    this.clicks = [...this.clicks.slice(-1), { before: handled ? this.snapshot() : null }];
    if (!handled) return;
    const state = this.store.getState();
    type Hit = SelectionEntry | { readonly source: 'casur-hydric'; readonly kind: 'channels' | 'drainage'; readonly id: string };
    const hits: Hit[] = [];
    this.map.forEachFeatureAtPixel(event.pixel, (feature, layer) => {
      const hydricKind = layer?.get('casurHydricKind');
      const hydricId = feature.get('casurHydricId');
      if ((hydricKind === 'channels' || hydricKind === 'drainage') && typeof hydricId === 'string') { hits.push({ source: 'casur-hydric', kind: hydricKind, id: hydricId }); return undefined; }
      const layerId = layer?.get('layerId');
      const featureId = feature.get('genericFeatureId');
      if (typeof layerId === 'string' && typeof featureId === 'string') { hits.push({ source: 'generic', layerId, kind: 'feature', id: featureId }); return undefined; }
      const fragmentId = feature.get('fragmentId');
      if (layer?.get('role') === 'master' && typeof fragmentId === 'string') {
        const entry = state.geometryEditSession
          ? masterEntryForFragment(state.model, fragmentId, 'fragment')
          : masterEntryForFragment(state.model, fragmentId, state.entityViewLevel);
        if (entry) hits.push(entry);
      }
      const entityId = feature.get('entityId'); const entityLevel = feature.get('entityLevel');
      if (layer?.get('role') === 'entity-presentation' && state.model && typeof entityId === 'string' && (entityLevel === 'finca' || entityLevel === 'suerte' || entityLevel === 'tablon')) {
        hits.push({ source: 'master', layerId: state.model.id, kind: entityLevel, id: entityId });
      }
      return undefined;
    }, { hitTolerance: 6 });
    if (state.geometryEditSession) {
      const allowed = entityFragmentIds(state.model!, state.geometryEditSession.entityKind, state.geometryEditSession.entityId);
      const candidate = hits.find((hit) => hit.source === 'master' && hit.kind === 'fragment' && allowed.includes(hit.id));
      this.store.update({ geometryEditSession: selectGeometryEditFragment(state.geometryEditSession, candidate?.id ?? null) }, { markDirty: false });
      return;
    }
    const hit = hits.find(({ source }) => source === 'casur-hydric') ?? hits[0];
    const pointer = event.originalEvent as PointerEvent;
    const additive = this.tool === 'select-multiple' || pointer.ctrlKey || pointer.metaKey;
    if (!hit) { if (!additive) this.clear(); return; }
    if (hit.source === 'casur-hydric') {
      this.store.update({ casurHydricSelection: { kind: hit.kind, featureId: hit.id }, selectionSet: emptySelectionSet(), selection: null, genericSelection: null }, { markDirty: false });
      return;
    }
    this.apply(updateSelectionSet(state.selectionSet, hit, additive), hit.layerId, { casurHydricSelection: null });
  }

  private snapshot(): SelectionSnapshot {
    const state = this.store.getState();
    return Object.fromEntries(SNAPSHOT_KEYS.map((key) => [key, state[key]])) as SelectionSnapshot;
  }

  private restoreBeforeDoubleClick(): void {
    const [first, second] = this.clicks; this.clicks = [];
    if (!first?.before || !second?.before || !isSelectionTool(this.tool)) return;
    const state = this.store.getState(); const before = first.before;
    if (SNAPSHOT_KEYS.every((key) => state[key] === before[key])) return;
    this.store.update(before, { markDirty: false });
  }

  private onBoxEnd(): void {
    const state = this.store.getState();
    const layer = state.layers.find((item) => item.id === state.activeLayerId);
    const geometry = this.dragBox.getGeometry();
    if (!layer || !geometry) { this.clear(); return; }
    const allowed = layer.kind === 'master'
      ? workScopeFragmentIds(state.model, state.workScope)
      : deriveDisplaySet(state).layerFeatureIds.get(layer.id);
    const entries = selectionEntriesInExtent(layer, geometry.getExtent(), state.entityViewLevel, allowed);
    this.apply(replaceSelectionSet(entries), layer.id);
  }

  /** One store update per selection (clicks used to issue two: clear the hydric selection, then select). */
  private apply(selectionSet: ReturnType<typeof replaceSelectionSet>, activeLayerId: string, patch: Partial<AppState> = {}): void {
    const legacy = legacySelectionForFocus(selectionSet.focus);
    this.store.update({
      ...patch,
      selectionSet,
      ...legacy,
      activeLayerId,
      activeFincaId: fincaIdForSelection(this.store.getState().model, legacy.selection),
    });
  }
}
